import styled from '@emotion/styled';
import { CardStyle } from '@shared/components/core/card/LegacyCard';
import Icons from '@shared/components/core/images/Icons';
import { CardSubtitleCentered, CardTitle } from '@shared/components/core/text/CardTypography';
import { CommandType, commandTypesDisplay } from '@shared/constants/CommandValues';
import { typographySize } from '@shared/constants/Styles';
import { getCommandDisplayName, getMacroFallbackName } from '@shared/hooks/helpers/profileUtils';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { useTranslation } from 'react-i18next';
import MacroEventDescription from './MacroEventDescription';
import MacroStepIcons from './MacroStepIcons';

interface IMacroCardProps {
    macro: MacroDisplayModel;
    showTitle?: boolean;
    children?: React.ReactNode;
    id?: string;
}

export default function MacroCard({ id, macro, showTitle = true, children, ...restProps }: IMacroCardProps) {
    const { t } = useTranslation();
    const getIconsForCommand = (commandType: CommandType) => {
        return commandTypesDisplay[commandType] || null;
    };
    const macroName = macro.name ? macro.name : getMacroFallbackName(macro.commands?.[0]?.command, t);

    return (
        <Card {...restProps} id={id}>
            <Content>
                {showTitle && <CardTitle>{macroName}</CardTitle>}
                <SummaryLeft>
                    <SummarySectionText>{t('common:when')}</SummarySectionText>
                    {/* <MacroCommandPreview commands={macro.commands} /> */}
                    {macro.commands?.map((c, i) => {
                        const icon = getIconsForCommand(c.commandType);
                        return (
                            <StepWrapperLeft key={`mcCommands_${i}`}>
                                {/* <IconStep name="downRightArrow" size={18} /> */}
                                <Icons size={32} name={icon.icon} ariaLabel={icon.alt} />
                                <SummaryDetailText data-testid={c.command}>
                                    {getCommandDisplayName(c.command, t)}
                                </SummaryDetailText>
                            </StepWrapperLeft>
                        );
                    })}
                </SummaryLeft>
                <SummaryRight>
                    <SummarySectionText>{t('common:thenMacro')}</SummarySectionText>
                    <MacroName>
                        {macroName}
                        <Icons name="logo" size={32} />
                    </MacroName>
                    {macro.events?.map((e, i) => {
                        return (
                            <StepWrapperRight key={`mcEvents_${i}`}>
                                <MacroStepIcons event={e} iconSize={24} />
                                <MacroEventDescription event={e} />
                                <IconStep name="downLeftArrow" size={18} />
                            </StepWrapperRight>
                        );
                    })}
                </SummaryRight>
                {children}
            </Content>
        </Card>
    );
}

const Content = styled.div`
    margin: auto;
    max-width: 800px;
    align-self: center;
`;
const IconStep = styled(Icons)`
    position: relative;
    top: -0.2em;
`;
const MacroName = styled(CardSubtitleCentered)`
    padding: 0 0 0.5rem 0;
    vertical-align: middle;
    gap: 0.2rem;
    justify-content: right;
    font-weight: 500;
`;

const Card = styled(CardStyle)`
    padding: 0.5rem 2rem 2rem 2rem;
    width: 100%;
`;

const StepWrapper = styled.div`
    display: flex;
    vertical-align: middle;
    align-items: center;
    gap: 0.2rem;
    width: 100%;
    padding: 0.2rem 0;
    color: ${(props) => props.theme.color.text};
`;

const StepWrapperLeft = styled(StepWrapper)`
    justify-content: left;
`;
const StepWrapperRight = styled(StepWrapper)`
    justify-content: right;
`;
const SummaryLeft = styled.div``;
const SummaryRight = styled.div`
    text-align: right;
`;

const SummarySectionText = styled.p`
    margin: 1em 0 0.5em;
    font-size: ${typographySize.default};
    font-weight: lighter;
`;
const SummaryDetailText = styled.p`
    white-space: pre-wrap;
`;
